import { Field, Input, Stack, Text, Textarea } from '@components/ui';
import {
  formatUsPhone,
  localUsDigits,
  nextUsPhoneDigits,
  toUsPhoneNumber,
} from '@features/auth/phoneNumber';
import { useHireJob } from '@features/buyer/hire/hire-job-context';
import { HireStepActions } from '@features/buyer/hire/hire-r8tr/hire-step-actions';
import type { HireStepNavigation } from '@features/buyer/hire/hire-r8tr/hireR8trTypes';
import { zodResolver } from '@hookform/resolvers/zod';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import Box from '@mui/material/Box';
import InputAdornment from '@mui/material/InputAdornment';
import type { SvgIconProps } from '@mui/material/SvgIcon';
import { type ComponentType, useEffect } from 'react';
import {
  Controller,
  useForm,
  type UseFormRegisterReturn,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  contactDetailsSchema,
  type ContactFormValues,
  SELLER_TYPES,
} from './contactDetailsSchema';

type SellerType = (typeof SELLER_TYPES)[number];

const [PRIVATE_KEY, DEALER_KEY] = SELLER_TYPES;

const SELLER_OPTIONS: {
  value: SellerType;
  titleKey: 'hire.privateSeller' | 'hire.dealer';
  subtitleKey: 'hire.privateSellerHint' | 'hire.dealerHint';
  Icon: ComponentType<SvgIconProps>;
}[] = [
  {
    value: PRIVATE_KEY,
    titleKey: 'hire.privateSeller',
    subtitleKey: 'hire.privateSellerHint',
    Icon: PersonOutlineIcon,
  },
  {
    value: DEALER_KEY,
    titleKey: 'hire.dealer',
    subtitleKey: 'hire.dealerHint',
    Icon: StorefrontOutlinedIcon,
  },
];

interface ContactDetailsProps extends HireStepNavigation {
  onPressNext: () => void;
}

const inputBinding = (
  registration: UseFormRegisterReturn
): Pick<UseFormRegisterReturn, 'name' | 'onBlur' | 'onChange'> & {
  inputRef: UseFormRegisterReturn['ref'];
} => ({
  name: registration.name,
  onBlur: registration.onBlur,
  onChange: registration.onChange,
  inputRef: registration.ref,
});

const ContactDetails = ({
  onPressNext,
  onBack,
  onCancel,
}: ContactDetailsProps): JSX.Element => {
  const { t } = useTranslation();
  const { createJob, saveJob } = useHireJob();
  const {
    control,
    register,
    watch,
    reset,
    setValue,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<ContactFormValues>({
    mode: 'onTouched',
    resolver: zodResolver(contactDetailsSchema),
    defaultValues: {
      contactType: PRIVATE_KEY,
      contactName: '',
      contactPhone: '',
      contactEmail: '',
      instruction: '',
    },
  });

  const activeKey = watch('contactType');
  const errorText = (message?: string): string | undefined =>
    message ? t(message) : undefined;

  useEffect(() => {
    reset({
      contactType:
        createJob.contactType === DEALER_KEY ? DEALER_KEY : PRIVATE_KEY,
      contactName: createJob.contactName || '',
      contactPhone: localUsDigits(createJob.contactNumber),
      contactEmail: createJob.contactEmail || '',
      instruction: createJob.specialInstructions || '',
    });
  }, [createJob, reset]);

  const onSubmit = (values: ContactFormValues): void => {
    saveJob({
      categoryId: createJob.categoryId,
      subCategoryId: createJob.subCategoryId,
      askingPrice: createJob.askingPrice,
      buyerCost: createJob.buyerCost,
      description: createJob.description,
      vinOrSerial: createJob.vinOrSerial,
      year: createJob.year,
      make: createJob.make,
      model: createJob.model,
      listing: createJob.listing,
      address: createJob.address,
      city: createJob.city,
      state: createJob.state,
      zip: createJob.zip,
      jobLatitude: createJob.jobLatitude,
      jobLongitude: createJob.jobLongitude,
      contactName: values.contactName,
      contactNumber: toUsPhoneNumber(values.contactPhone),
      contactEmail: values.contactEmail,
      contactType: values.contactType,
      specialInstructions: values.instruction,
    });
    onPressNext();
  };

  return (
    <Stack gap="xs" sx={{ flex: 1 }}>
      <Field label={t('hire.whoDealingWith')}>
        <Box
          role="radiogroup"
          aria-label={t('hire.sellerTypeAria')}
          sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: '1fr 1fr' }}
        >
          {SELLER_OPTIONS.map(({ value, titleKey, subtitleKey, Icon }) => {
            const selected = activeKey === value;
            return (
              <Box
                key={value}
                component="button"
                type="button"
                role="radio"
                aria-checked={selected}
                onClick={() =>
                  setValue('contactType', value, { shouldValidate: true })
                }
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  p: 1.75,
                  borderRadius: 2,
                  border: 1,
                  borderColor: selected ? 'text.primary' : 'divider',
                  bgcolor: selected ? 'action.selected' : 'background.paper',
                  color: 'text.primary',
                  font: 'inherit',
                  textAlign: 'left',
                  cursor: 'pointer',
                  transition:
                    'border-color 0.2s ease, background-color 0.2s ease',
                  '&:hover': { borderColor: 'text.secondary' },
                }}
              >
                <Icon
                  sx={{
                    fontSize: 22,
                    color: selected ? 'primary.main' : 'text.secondary',
                  }}
                />
                <Box sx={{ minWidth: 0 }}>
                  <Text
                    as="span"
                    variant="label"
                    sx={{ display: 'block', fontWeight: 700 }}
                  >
                    {t(titleKey)}
                  </Text>
                  <Text as="span" variant="caption" tone="muted">
                    {t(subtitleKey)}
                  </Text>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Field>

      <Field
        label={t('hire.contactNameShort')}
        htmlFor="contact-name"
        error={errorText(errors.contactName?.message)}
      >
        <Input
          id="contact-name"
          type="text"
          placeholder={t('hire.contactNamePlaceholder')}
          autoComplete="name"
          invalid={Boolean(errors.contactName)}
          {...inputBinding(register('contactName'))}
        />
      </Field>

      <Box
        sx={{
          display: 'grid',
          columnGap: 2,
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
        }}
      >
        <Field
          label={t('hire.contactPhoneShort')}
          htmlFor="contact-phone"
          error={errorText(errors.contactPhone?.message)}
        >
          <Controller
            name="contactPhone"
            control={control}
            render={({ field }) => (
              <Input
                id="contact-phone"
                type="tel"
                name={field.name}
                inputRef={field.ref}
                placeholder={t('common.phonePlaceholder')}
                autoComplete="tel-national"
                invalid={Boolean(errors.contactPhone)}
                value={formatUsPhone(field.value)}
                onChange={(event) =>
                  field.onChange(
                    nextUsPhoneDigits(field.value, event.target.value)
                  )
                }
                onBlur={field.onBlur}
                inputProps={{ inputMode: 'numeric' }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      {t('common.countryCode')}
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />
        </Field>

        <Field
          label={t('hire.contactEmailShort')}
          htmlFor="contact-email"
          error={errorText(errors.contactEmail?.message)}
        >
          <Input
            id="contact-email"
            type="email"
            placeholder={t('hire.contactEmailPlaceholder')}
            autoComplete="email"
            invalid={Boolean(errors.contactEmail)}
            {...inputBinding(register('contactEmail'))}
          />
        </Field>
      </Box>

      <Field label={t('hire.specialInstructionsOptional')}>
        <Textarea
          placeholder={t('hire.specialInstructionsPlaceholder')}
          minRows={3}
          {...inputBinding(register('instruction'))}
        />
      </Field>

      <HireStepActions
        onBack={onBack}
        onCancel={onCancel}
        onPrimary={handleSubmit(onSubmit)}
        primaryDisabled={!isValid}
      />
    </Stack>
  );
};

export default ContactDetails;
